import React, { FC, useCallback, useEffect, useMemo, useState } from 'react';
import {
  Button,
  Checkbox,
  DatePicker,
  Divider,
  Input,
  InputNumber,
  Tooltip,
  Popover,
  Radio,
  Select,
} from 'antd';
import { CloseCircleOutlined, PlusCircleOutlined } from '@ant-design/icons';
import {
  FilterField,
  FilterOperatorDef,
  FilterValue,
} from './filter-panel-types';
import dayjs from 'dayjs';
import { useBusinessContext } from '../../context/BusinessContext';

type Props = {
  field: FilterField;
  value?: FilterValue;
  onChange?: (value?: FilterValue) => void;
};

const DateInput2: FC<{
  value?: string;
  onChange?: (value?: string) => void;
}> = ({ value, onChange }) => {
  return (
    <DatePicker
      value={value ? dayjs(value, 'YYYY-MM-DD') : undefined}
      onChange={(v) => onChange?.(v ? v.format('YYYY-MM-DD') : undefined)}
    />
  );
};

export const FilterContent: FC<Props> = ({ field, value, onChange }) => {
  const operatorOptions = useMemo(() => {
    return field.operators.map((op) => ({
      value: op,
      label: FilterOperatorDef[op],
    }));
  }, [field.operators]);

  const [vl, setVl] = useState<any>();
  const [vl2, setVl2] = useState<any>();
  useEffect(() => {
    if (value?.operator === 'between') {
      setVl(((value?.value as any) ?? [])[0]);
      setVl2(((value?.value as any) ?? [])[1]);
    } else {
      setVl(value?.value);
    }
  }, [value]);
  const [op, setOp] = useState<any>(field.operators[0]);

  const [COMP, props]: [any, any] = useMemo(() => {
    if (field.type === 'string') {
      return [Input, {}];
    } else if (field.type === 'number') {
      return [InputNumber, { style: { minWidth: 100 } }];
    } else if (field.type === 'select') {
      return [
        Select,
        {
          options: field.options,
          showSearch: true,
          filterOption: (input: any, option: any) =>
            (option?.label ?? '').toLowerCase().includes(input.toLowerCase()),
        },
      ];
    } else if (field.type === 'date') {
      return [DateInput2, {}];
    } else if (field.type === 'radio') {
      return [
        Radio.Group,
        {
          options: field.options,
          style: {
            display: 'flex',
            flexDirection: 'column',
            gap: 8,
          },
        },
      ];
    } else if (field.type === 'checkbox') {
      return [
        Checkbox.Group,
        {
          options: field.options,
          style: {
            display: 'flex',
            flexDirection: 'column',
            gap: 8,
          },
        },
      ];
    }
    return [Input, {}];
  }, [field.options, field.type]);

  const onApply = useCallback(() => {
    if (vl === undefined || vl === null || vl === '') {
      onChange?.(undefined);
    } else {
      if (op === 'between') {
        if (vl2 === undefined || vl2 === null || vl2 === '') {
          onChange?.(undefined);
        } else {
          onChange?.({ value: [vl, vl2], name: field.name, operator: op });
        }
      } else {
        onChange?.({ value: vl, name: field.name, operator: op });
        setVl2(undefined);
      }
    }
  }, [field.name, onChange, op, vl, vl2]);

  return (
    <div className="filter-content">
      <div style={{ fontWeight: 'bold' }}>Filter by {field.label}</div>
      {operatorOptions.length > 1 && (
        <Select
          options={operatorOptions}
          variant="filled"
          value={op}
          onChange={setOp}
        />
      )}
      {op !== 'between' && (
        <COMP
          {...props}
          value={vl}
          onChange={(v: any) => setVl(v.target ? v.target.value : v)}
        />
      )}
      {op === 'between' && (
        <div>
          <COMP
            {...props}
            value={vl}
            onChange={(v: any) => setVl(v?.target ? v.target.value : v)}
          />
          {' - '}
          <COMP
            {...props}
            value={vl2}
            onChange={(v: any) => setVl2(v?.target ? v.target.value : v)}
          />
        </div>
      )}
      <Button
        type="primary"
        onClick={onApply}
        disabled={
          vl === undefined ||
          vl === null ||
          vl === '' ||
          (op === 'between' &&
            (vl2 === undefined || vl2 === null || vl2 === ''))
        }
      >
        Apply
      </Button>
    </div>
  );
};

const arrayToText = (arr: any[]) => {
  if (arr.length > 2) {
    return `${arr[0]}, ${arr[1]}, +${arr.length - 2}`;
  }
  return arr.join(', ');
};

export const FilterItem: FC<Props> = ({ field, value, onChange }) => {
  const [visible, setVisible] = useState<boolean>(false);
  const { states, suburbs, zones, sites, collectionType } =
    useBusinessContext();
  const isDisabled = field.disabled;
  const tooltipText = field.disabledReason;
  const getDisplayName = useCallback(
    (id: any) => {
      if (field.masterDataType) {
        if (field.masterDataType === 'states') {
          return states[id].name;
        }
        if (field.masterDataType === 'suburbs') {
          return suburbs[id].name;
        }
        if (field.masterDataType === 'zone') {
          return zones[id].name;
        }
        if (field.masterDataType === 'site') {
          return sites[id].name;
        }
        if (field.masterDataType === 'collectionType') {
          return collectionType[id]?.name ?? id;
        }
        return id;
      }
      // Options-based fields (select/radio/checkbox): show the selected option's
      // label, not the raw value (e.g. a contract's name instead of its id).
      if (field.options) {
        const opt = field.options.find((o) => String(o.value) === String(id));
        if (opt) return opt.label;
      }
      return id;
    },
    [
      collectionType,
      field.masterDataType,
      field.options,
      sites,
      states,
      suburbs,
      zones,
    ],
  );

  return (
    <Tooltip title={isDisabled ? tooltipText : undefined} placement="bottom">
      <Popover
        placement="bottomLeft"
        content={
          !isDisabled && visible ? (
            <FilterContent
              field={field}
              onChange={(v) => {
                onChange?.(v);
                setVisible(false);
              }}
              value={value}
            />
          ) : (
            <></>
          )
        }
        trigger={isDisabled ? [] : 'click'}
        open={isDisabled ? false : visible}
        onOpenChange={(open) => {
          if (!isDisabled) {
            setVisible(open);
          }
        }}
      >
        <div
          className={`filter-item ${value !== undefined ? 'filter-item-selected' : ''}`}
          style={{
            opacity: isDisabled ? 0.6 : 1,
            cursor: isDisabled ? 'not-allowed' : 'pointer',
          }}
        >
          <Button
            className="filter-item-button"
            shape="circle"
            type="text"
            variant="text"
            disabled={isDisabled}
            danger={!!value}
            icon={value ? <CloseCircleOutlined /> : <PlusCircleOutlined />}
            onClick={(e) => {
              if (isDisabled) {
                e.stopPropagation();
                e.preventDefault();
                return;
              }
              if (value !== undefined) {
                e.stopPropagation();
                e.preventDefault();
                setVisible(false);
                onChange?.(undefined);
              }
            }}
          />
          <div
            className={`filter-label ${value !== undefined ? 'filter-label-active' : ''}`}
          >
            {field.label}
          </div>
          {value !== undefined && (
            <>
              <Divider type="vertical" style={{ borderColor: 'black' }} />
              {value.operator !== 'eq' && value.operator !== 'between' && (
                <div className="filter-operator-text">
                  {FilterOperatorDef[value.operator]}{' '}
                </div>
              )}
              {value.operator === 'between' ? (
                <div className="filter-item-value">
                  {(value.value as any)[0]} - {(value.value as any)[1]}
                </div>
              ) : (
                <div className="filter-item-value">
                  {Array.isArray(value.value)
                    ? arrayToText(value?.value?.map((c) => getDisplayName(c)))
                    : getDisplayName(value.value)}
                </div>
              )}
            </>
          )}
        </div>
      </Popover>
    </Tooltip>
  );
};
